import { useState } from "react";

import { usePreviewRunEventQuery } from "@/api/previewInsights";
import {
  compactForDisplay,
  type LabRawRow,
  type LabRunModel,
} from "@/lib/labRunModel";
import {
  EVENT_KIND_LABELS,
  EVENT_STATUSES,
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  TRACE_EVENT_KINDS,
} from "@/lib/previewDecisions";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { describeDetailFailure } from "@/ui/preview/PreviewRunConversation";
import { EmptyState } from "@/ui/states";

/**
 * Every event of the run as the list read delivered it (hashes and sizes, no
 * bodies), filterable by kind and status. The full event JSON, which can carry
 * conversation or prompt text, is read only after an explicit press, shown as
 * inert text and held by nothing but the query that fetched it.
 */

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

const PAGE = 200;
const SELECT =
  "rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground";

function statusTone(status: string) {
  return (
    (
      EVENT_STATUS_TONES as Record<
        string,
        "neutral" | "outline" | "warning" | "destructive"
      >
    )[status] ?? "outline"
  );
}

function FullEventJson({
  projectId,
  previewRunRef,
  eventRef,
}: {
  projectId: string;
  previewRunRef: string;
  eventRef: string;
}) {
  // The caller keys this on eventRef, so choosing another event resets the flag.
  const [requested, setRequested] = useState(false);
  const detail = usePreviewRunEventQuery(projectId, previewRunRef, eventRef, {
    enabled: requested,
  });

  if (!requested) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setRequested(true)}
      >
        Load full event JSON
      </Button>
    );
  }
  if (detail.isPending) {
    return <p className="text-sm text-muted-foreground">Reading this event…</p>;
  }
  if (detail.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        This event could not be read{describeDetailFailure(detail.error)}.{" "}
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => void detail.refetch()}
        >
          Try again
        </button>
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        This view can contain conversation or prompt text; it is not stored.
      </p>
      <pre
        dir="auto"
        aria-label="Full event JSON"
        className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-xs text-foreground"
      >
        {JSON.stringify(detail.data, null, 2)}
      </pre>
    </div>
  );
}

export function LabRawEventsTab({ projectId, model, onSelectTurn }: Props) {
  const [kind, setKind] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE);

  const kindCounts = new Map<string, number>();
  for (const row of model.raw)
    kindCounts.set(row.kind, (kindCounts.get(row.kind) ?? 0) + 1);

  const filtered = model.raw.filter(
    (row) =>
      (kind === "all" || row.kind === kind) &&
      (status === "all" || row.status === status),
  );
  const shown = filtered.slice(0, limit);
  const selected: LabRawRow | null =
    model.raw.find((row) => row.eventRef === selectedRef) ?? null;

  const choose = (row: LabRawRow) => {
    setSelectedRef(row.eventRef);
    if (row.turnNumber !== null) onSelectTurn(row.turnNumber);
  };

  return (
    <section aria-labelledby="lab-raw-heading" className="space-y-4">
      <h2
        id="lab-raw-heading"
        className="text-base font-semibold text-foreground"
      >
        Raw events
      </h2>

      {model.raw.length === 0 ? (
        <EmptyState
          title="No events recorded yet"
          description="Events appear here as the run captures them."
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Kind
              <select
                className={SELECT}
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value);
                  setLimit(PAGE);
                }}
              >
                <option value="all">All kinds ({model.raw.length})</option>
                {TRACE_EVENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {EVENT_KIND_LABELS[k]} ({kindCounts.get(k) ?? 0})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Status
              <select
                className={SELECT}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setLimit(PAGE);
                }}
              >
                <option value="all">All statuses</option>
                {EVENT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {EVENT_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <p className="text-sm text-muted-foreground" role="status">
            Showing {shown.length} of {filtered.length} events
          </p>

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No event matches this filter.
            </p>
          ) : (
            <ul className="space-y-1">
              {shown.map((row) => (
                <li key={row.eventRef}>
                  <button
                    type="button"
                    aria-pressed={row.eventRef === selectedRef}
                    className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-card px-3 py-2 text-start text-sm hover:bg-muted aria-pressed:border-ring aria-pressed:bg-accent"
                    onClick={() => choose(row)}
                  >
                    <span
                      dir="ltr"
                      className="font-mono text-xs text-muted-foreground"
                    >
                      #{row.sequence}
                    </span>
                    <span className="text-muted-foreground">
                      {EVENT_KIND_LABELS[
                        row.kind as keyof typeof EVENT_KIND_LABELS
                      ] ?? row.kind}
                    </span>
                    <span
                      dir="auto"
                      className="min-w-0 break-words text-foreground"
                    >
                      {row.label}
                    </span>
                    <Badge tone={statusTone(row.status)}>
                      {row.statusLabel}
                    </Badge>
                    <span className="text-muted-foreground">
                      {row.turnNumber !== null
                        ? `turn ${row.turnNumber}`
                        : "no turn"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {filtered.length > shown.length ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLimit(limit + PAGE)}
            >
              Show more
            </Button>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Selected event</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              {selected === null ? (
                <p className="text-sm text-muted-foreground">
                  Select an event to see its record.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Compact record as listed: bodies are hashes and sizes only.
                  </p>
                  <pre
                    dir="ltr"
                    aria-label="Compact event record"
                    className="max-h-80 overflow-x-auto rounded-md bg-muted p-3 text-xs text-foreground"
                  >
                    {compactForDisplay(selected.compact)}
                  </pre>
                  <FullEventJson
                    key={selected.eventRef}
                    projectId={projectId}
                    previewRunRef={model.previewRunRef}
                    eventRef={selected.eventRef}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </section>
  );
}
